import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, BookOpen, Star, ArrowRight, Tag } from 'lucide-react';

const ArticleCard = ({ article, variant = 'standard' }) => {
  const readTime = Math.max(1, Math.round((article.word_count || 0) / 250));
  const categoryName = article.category ? article.category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  if (variant === 'featured') {
    return (
      <Link
        to={`/academy/${article.slug}`}
        data-testid={`article-card-${article.slug}`}
        className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#115E59] hover:shadow-lg transition-all duration-300"
      >
        <div className="p-7">
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
              <Star className="h-3 w-3" />
              Guide
            </span>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{categoryName}</span>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-3 leading-snug group-hover:text-[#115E59] transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-3">{article.excerpt}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{readTime} min</span>
              <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{(article.word_count || 0).toLocaleString()} words</span>
            </div>
            <ArrowRight className="h-4 w-4 text-[#115E59] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/academy/${article.slug}`}
        data-testid={`article-card-${article.slug}`}
        className="group block"
      >
        <div className="bg-white border border-slate-200 rounded-xl p-5 h-full hover:border-[#115E59]/40 hover:shadow-sm transition-all duration-200">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">{categoryName}</span>
          <h3 className="text-sm font-bold text-slate-900 mb-2 leading-snug group-hover:text-[#115E59] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
          <span className="text-xs font-semibold text-[#115E59] flex items-center gap-1">
            Read article <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </div>
      </Link>
    );
  }

  // standard variant
  return (
    <Link
      to={`/academy/${article.slug}`}
      data-testid={`article-card-${article.slug}`}
      className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#115E59]/40 hover:shadow-md transition-all duration-300"
    >
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/academy/category/${article.category}`; }}
            className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full hover:bg-[#115E59]/10 hover:text-[#115E59] transition-colors cursor-pointer"
          >
            <Tag className="h-2.5 w-2.5" />
            {categoryName}
          </span>
          {article.is_cornerstone && (
            <span className="bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">Guide</span>
          )}
        </div>
        <h3 className="text-base font-bold text-slate-900 mb-2 leading-snug group-hover:text-[#115E59] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {article.title}
        </h3>
        <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{readTime} min</span>
          </div>
          <span className="text-xs font-semibold text-[#115E59] flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            Read <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ArticleCard;
